import { useState, useMemo, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  RefreshCw,
  UserPlus,
  X,
  Search,
  FolderTree,
} from "lucide-react";
import { toast } from "@fox1ck-org/ui/toast";
import {
  useGroup,
  useGroupChildren,
  useGroupMembers,
  useAddGroupMember,
  useRemoveGroupMember,
} from "@/hooks/use-groups";
import { useUsers } from "@/hooks/use-users";
import { PageHeader } from "@/components/shared/page-header";
import { Avatar, AvatarFallback, Badge, Button, Card, CardContent, CardHeader, CardTitle, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, Input, Skeleton } from "@fox1ck-org/ui";

const PAGE_SIZE = 50;

function initials(user: { firstName?: string; lastName?: string; username?: string }) {
  const first = user.firstName?.[0] ?? "";
  const last = user.lastName?.[0] ?? "";
  if (first || last) return (first + last).toUpperCase();
  return (user.username?.[0] ?? "?").toUpperCase();
}

export default function GroupDetail() {
  const { id } = useParams<{ id: string }>();
  const [page, setPage] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [debouncedUserSearch, setDebouncedUserSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedUserSearch(userSearch), 300);
    return () => clearTimeout(t);
  }, [userSearch]);

  const { data: groupData, isLoading: groupLoading } = useGroup(id ?? "");
  const { data: childrenData } = useGroupChildren(id ?? "");
  const {
    data: membersData,
    isLoading: membersLoading,
    refetch: refetchMembers,
  } = useGroupMembers(id ?? "", { first: page * PAGE_SIZE, max: PAGE_SIZE });
  const { data: candidateUsersData } = useUsers({
    search: debouncedUserSearch || undefined,
    max: 20,
  });

  const addMember = useAddGroupMember(id ?? "");
  const removeMember = useRemoveGroupMember(id ?? "");

  const group = groupData?.group;
  const children = childrenData?.children ?? [];
  const members = useMemo(() => membersData?.members ?? [], [membersData]);
  const memberIdSet = useMemo(() => new Set(members.map((m) => m.id)), [members]);
  const candidates = useMemo(
    () => (candidateUsersData?.users ?? []).filter((u) => !memberIdSet.has(u.id)),
    [candidateUsersData, memberIdSet],
  );

  if (groupLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  if (!group) {
    return (
      <div className="space-y-4">
        <Link to="/groups" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> К группам
        </Link>
        <Card>
          <CardContent className="p-6 text-muted-foreground">Группа не найдена.</CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/groups"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4" /> К группам
        </Link>
        <PageHeader
          title={group.name}
          description={group.path ?? ""}
        >
          <Button variant="ghost" size="icon" onClick={() => refetchMembers()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setAddOpen(true)}
            className="gap-2"
          >
            <UserPlus className="h-4 w-4" />
            Добавить участника
          </Button>
        </PageHeader>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Участники
                <Badge variant="neutral" className="ml-2">
                  {members.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {membersLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : members.length === 0 ? (
                <div className="text-sm text-muted-foreground py-8 text-center">
                  В группе пока нет участников.
                </div>
              ) : (
                <div className="space-y-2">
                  {members.map((u) => (
                    <div
                      key={u.id}
                      className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2"
                    >
                      <Link
                        to={`/users/${u.id}`}
                        className="flex items-center gap-3 hover:text-primary flex-1"
                      >
                        <Avatar className="h-8 w-8">
                          <AvatarFallback>{initials(u)}</AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-foreground">
                            {u.firstName} {u.lastName}
                          </span>
                          <span className="text-xs text-muted-foreground">{u.email ?? u.username}</span>
                        </div>
                      </Link>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.preventDefault();
                          removeMember.mutate(u.id, {
                            onSuccess: () => toast.success("Участник удалён"),
                            onError: (err) => toast.error(`Не удалось удалить: ${err.message}`),
                          });
                        }}
                        disabled={removeMember.isPending}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {members.length === PAGE_SIZE && (
                <div className="flex justify-between mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                  >
                    Назад
                  </Button>
                  <span className="text-sm text-muted-foreground self-center">Page {page + 1}</span>
                  <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)}>
                    Вперёд
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">О группе</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <div className="text-xs text-muted-foreground mb-1">ID</div>
                <code className="text-xs font-mono break-all">{group.id}</code>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Путь</div>
                <code className="text-xs font-mono break-all">{group.path ?? ""}</code>
              </div>
            </CardContent>
          </Card>

          {children.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm">
                  <FolderTree className="h-4 w-4" />
                  Дочерние группы
                  <Badge variant="neutral" className="ml-1">{children.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  {children.map((c) => (
                    <Link
                      key={c.id}
                      to={`/groups/${c.id}`}
                      className="block rounded-md px-2 py-1.5 text-sm text-foreground hover:bg-muted/50 hover:text-primary"
                    >
                      {c.name}
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Участник группы {group.name}</DialogTitle>
            <DialogDescription>
              Найдите пользователя и выберите его. Добавление срабатывает сразу, убрать можно кнопкой ×.
            </DialogDescription>
          </DialogHeader>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Поиск по логину, почте, имени…"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              autoFocus
              className="pl-10"
            />
          </div>

          <div className="space-y-1 max-h-[320px] overflow-y-auto">
            {candidates.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">
                {debouncedUserSearch
                  ? "Никого не нашлось или все уже в группе."
                  : "Начните вводить, чтобы найти пользователя."}
              </div>
            ) : (
              candidates.map((u) => (
                <button
                  key={u.id}
                  onClick={() =>
                    addMember.mutate(u.id, {
                      onSuccess: () => {
                        toast.success(`Добавлен ${u.email ?? u.username}`);
                        setUserSearch("");
                      },
                      onError: (err) => toast.error(`Не удалось добавить: ${err.message}`),
                    })
                  }
                  disabled={addMember.isPending}
                  className="w-full flex items-center gap-3 rounded-md px-3 py-2 hover:bg-muted/50 text-left disabled:opacity-50"
                >
                  <Avatar className="h-7 w-7">
                    <AvatarFallback>{initials(u)}</AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col flex-1">
                    <span className="text-sm font-medium text-foreground">
                      {u.firstName} {u.lastName}
                    </span>
                    <span className="text-xs text-muted-foreground">{u.email ?? u.username}</span>
                  </div>
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
